import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { AVAILABLE_ICONS, COLOR_PRESETS, resolveIcon } from "./utils";

// Re-export generic form primitives from the shared library.
// Consumers of this module (QuickActionsManager, TaskTemplatesManager, etc.)
// continue to import from "./FormWidgets" without changes.
export { FormField, FormInput, FormTextarea } from "@/components/shared";

export function IconPicker({
  value,
  onChange,
}: Readonly<{
  value: string;
  onChange: (v: string) => void;
}>) {
  const [open, setOpen] = useState(false);
  const Icon = resolveIcon(value);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm hover:border-brand-primary/40 transition-colors"
      >
        <Icon className="h-4 w-4 text-content-primary" />
        <span className="text-content-secondary">{value}</span>
        <ChevronDown className="h-3 w-3 text-content-quaternary" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 bg-surface-primary border border-border-primary rounded-lg shadow-xl p-2 grid grid-cols-6 gap-1 max-h-48 overflow-auto w-64">
          {AVAILABLE_ICONS.map((name) => {
            const Ic = resolveIcon(name);
            return (
              <button
                key={name}
                type="button"
                onClick={() => {
                  onChange(name);
                  setOpen(false);
                }}
                title={name}
                className={`p-2 rounded-md hover:bg-interactive-hover transition-colors ${
                  value === name ? "bg-brand-primary/20 ring-1 ring-brand-primary" : ""
                }`}
              >
                <Ic className="h-4 w-4 text-content-primary mx-auto" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ColorPicker({
  value,
  onChange,
  presets = COLOR_PRESETS,
  isGradient = false,
}: Readonly<{
  value: string;
  onChange: (v: string) => void;
  presets?: { label: string; value: string; swatch: string }[];
  isGradient?: boolean;
}>) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {presets.map((c) => (
        <button
          key={c.value}
          type="button"
          onClick={() => onChange(c.value)}
          title={c.label}
          className={`w-7 h-7 rounded-md border-2 transition-colors flex items-center justify-center ${
            value === c.value ? "border-brand-primary" : "border-transparent hover:border-border-secondary"
          }`}
        >
          {isGradient ? (
            <div className={`w-4 h-4 rounded-full bg-linear-to-br ${c.swatch}`} />
          ) : (
            <div className={`w-4 h-4 rounded-full ${c.swatch}`} />
          )}
        </button>
      ))}
    </div>
  );
}
